import './RoleStageActionFlows.scss';
import Container from 'components/molecules/Container/Container';
import NavbarWithBackLink from 'components/molecules/NavbarWithBackLink/NavbarWithBackLink';
import { HelmetAndPageAnnouncer } from 'components/organisms/HelmetAndPageAnnouncer/HelmetAndPageAnnouncer';
import TestIntro from 'components/organisms/TestIntro/TestIntro';
import { appSwitchRoutes } from 'constants/routes';
import {
  RssAssessmentType,
  SupportedLocale,
  TestStatus,
  useUserQuery,
} from 'generated/graphql';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Loader } from '@spotted-zebra-uk/ui-components';
import RoleSpecificSoftSkillsAssessment from 'views/RoleSpecificSkillsAssessment/RoleSpecificSoftSkillsAssessment';
import RoleSpecificTechnicalSkillsAssessment from 'views/RoleSpecificSkillsAssessment/RoleSpecificTechnicalSkillsAssessment';
import { TRssAssessmentFlowActions } from 'views/RoleSpecificSkillsAssessment/types';
import config from 'views/Test/TestInfo/TestInfo.constants';
import { useRoleStageActionsFlow } from './useRoleStageActionsFlow';

type TRoutingState = {
  /** Set when chaining soft → technical so the intro isn't shown twice. */
  skipIntro?: boolean;
};

const RoleStageRssAssessment: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { rssAssessmentCandidateId, rssAssessmentType } = useParams();
  const {
    roleId,
    stageId,
    role,
    stage,
    loading,
    exiting,
    actionsUrl,
    thankYouUrl,
  } = useRoleStageActionsFlow();

  const { skipIntro } = (location.state as TRoutingState) || {};
  const [started, setStarted] = useState(!!skipIntro);

  const userQueryResponse = useUserQuery();
  const assessmentLocale =
    userQueryResponse.data?.User.preferredLocale.locale ||
    SupportedLocale.English;

  if (loading) {
    return (
      <Container className="page-loader-wrapper">
        <Loader variant="bubbles" />
      </Container>
    );
  }

  if (exiting || !role || !stage || !rssAssessmentCandidateId) {
    return null;
  }

  if (!started) {
    const singleTestConfig = config['selfSkillAsessment']({});
    const MainIcon = singleTestConfig.icon;

    return (
      <Container className="TestInfoContainer">
        <NavbarWithBackLink
          to={actionsUrl}
          ariaLabel={t('assessment.returnToAllAssessmentActions')}
          label={t('assessment.actions')}
        />
        <HelmetAndPageAnnouncer
          pageTitle={t('test.instructions.skillsSelfAssessment.name')}
        />
        <TestIntro
          MainIcon={MainIcon}
          type={singleTestConfig.key}
          assessmentLocale={assessmentLocale}
          title={singleTestConfig.title}
          notes={singleTestConfig.notes}
          testStatus={TestStatus.NotStarted}
          hasPractice={false}
          onStart={() => setStarted(true)}
          onStartPracticeTest={() => {}}
          testTimePerQuestion={2}
        />
      </Container>
    );
  }

  const flowActions: TRssAssessmentFlowActions = {
    exitUrl: actionsUrl,
    onChainNext: (nextRssAssessmentCandidateId, nextRssAssessmentType) =>
      navigate(
        appSwitchRoutes.roleStageRssAssessment.getUrl(
          roleId,
          stageId,
          nextRssAssessmentCandidateId,
          nextRssAssessmentType
        ),
        { state: { skipIntro: true } }
      ),
    onFinished: () => navigate(thankYouUrl),
  };

  const viewProps = {
    rssAssessmentCandidateId: Number(rssAssessmentCandidateId),
    stageCandidateId: stage.stageCandidateId,
    technicalRssAssessmentAction: stage.actions.technicalRssAssessmentAction,
    flowActions,
  };

  return rssAssessmentType === RssAssessmentType.SoftSkill ? (
    <RoleSpecificSoftSkillsAssessment {...viewProps} />
  ) : (
    <RoleSpecificTechnicalSkillsAssessment {...viewProps} />
  );
};

export default RoleStageRssAssessment;
